(() => {
  const sliderSelector = '[data-featured-services-slider]';
  const mobileQuery = window.matchMedia('(max-width: 1024px)');
  const instances = new Map();

  const initializeSlider = (slider) => {
    if (instances.has(slider)) return;

    const track = slider.querySelector('[data-featured-services-track]');
    const viewport = slider.querySelector('[data-featured-services-viewport]');
    const slides = Array.from(track?.children || []);
    const controls = slider.querySelector('[data-featured-services-controls]');
    const desktopControls = slider.querySelector('.our-services__slider-desktop-controls');
    const dotsContainer = slider.querySelector('.our-services__slider-dots');
    const previousButton = slider.querySelector('[data-featured-services-prev]');
    const nextButton = slider.querySelector('[data-featured-services-next]');
    const count = slider.querySelector('[data-featured-services-count]');
    const dots = Array.from(slider.querySelectorAll('[data-featured-services-dot]'));

    if (!track || slides.length < 2) return;

    let activeIndex = 0;
    let timer;
    let mode = '';
    const resizeObserver = new ResizeObserver(() => {
      if (mode === 'vertical') window.requestAnimationFrame(update);
    });

    const update = () => {
      const nextMode = mobileQuery.matches ? 'vertical' : slides.length > 3 ? 'horizontal' : '';
      if (!nextMode) {
        slider.classList.remove('is-slider', 'is-vertical');
        track.style.removeProperty('transform');
        track.style.removeProperty('width');
        viewport.style.removeProperty('height');
        viewport.style.removeProperty('overflow');
        slides.forEach((slide) => {
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('inert');
        });
        controls.hidden = true;
        window.clearInterval(timer);
        mode = '';
        return;
      }

      if (mode !== nextMode) {
        activeIndex = 0;
        mode = nextMode;
      }

      slider.classList.add('is-slider');
      slider.classList.toggle('is-vertical', mode === 'vertical');
      controls.hidden = false;
      desktopControls.hidden = mode === 'vertical';
      dotsContainer.hidden = mode !== 'vertical';
      if (mode === 'vertical') viewport.style.overflow = 'hidden';

      const visibleSlides = mode === 'horizontal' ? 3 : 1;
      const lastIndex = Math.max(0, slides.length - visibleSlides);
      const pageStarts = [];
      for (let index = 0; index < slides.length; index += visibleSlides) {
        const pageStart = Math.min(index, lastIndex);
        if (pageStarts[pageStarts.length - 1] !== pageStart) pageStarts.push(pageStart);
        if (pageStart === lastIndex) break;
      }
      activeIndex = Math.min(activeIndex, lastIndex);
      const activePageIndex = Math.max(0, pageStarts.indexOf(activeIndex));
      slides.forEach((slide, index) => {
        const isVisible = index >= activeIndex && index < activeIndex + visibleSlides;
        slide.setAttribute('aria-hidden', String(!isVisible));
        slide.toggleAttribute('inert', !isVisible);
      });

      if (mode === 'vertical') {
        const slideTop = slides[activeIndex].getBoundingClientRect().top - slides[0].getBoundingClientRect().top;
        track.style.transform = `translate3d(0, -${slideTop}px, 0)`;
        viewport.style.setProperty('height', `${slides[activeIndex].getBoundingClientRect().height}px`, 'important');
        viewport.style.setProperty('overflow', 'hidden', 'important');
        track.style.width = '100%';
      } else {
        const slideWidth = slides[0].getBoundingClientRect().width;
        const gap = Number.parseFloat(getComputedStyle(track).gap) || 0;
        track.style.transform = `translate3d(-${activeIndex * (slideWidth + gap)}px, 0, 0)`;
        track.style.width = '100%';
        viewport.style.removeProperty('height');
      }

      if (previousButton && nextButton && count) {
        previousButton.disabled = activePageIndex === 0;
        nextButton.disabled = activePageIndex === pageStarts.length - 1;
        count.textContent = `${activePageIndex + 1} / ${pageStarts.length}`;
      }
      dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === activeIndex));
      });
    };

    const move = (direction) => {
      const visibleSlides = mode === 'horizontal' ? 3 : 1;
      if (mode === 'horizontal') {
        const lastIndex = Math.max(0, slides.length - visibleSlides);
        const pageStarts = [];
        for (let index = 0; index < slides.length; index += visibleSlides) {
          const pageStart = Math.min(index, lastIndex);
          if (pageStarts[pageStarts.length - 1] !== pageStart) pageStarts.push(pageStart);
          if (pageStart === lastIndex) break;
        }
        const pageIndex = pageStarts.indexOf(activeIndex);
        activeIndex = pageStarts[Math.max(0, Math.min(pageIndex + direction, pageStarts.length - 1))];
      } else {
        activeIndex = (activeIndex + direction + slides.length) % slides.length;
      }
      update();
    };

    const restartTimer = () => {
      window.clearInterval(timer);
      if (mode === 'vertical') {
        timer = window.setInterval(() => {
          activeIndex = activeIndex >= slides.length - 1 ? 0 : activeIndex + 1;
          update();
        }, 3500);
      }
    };

    previousButton?.addEventListener('click', () => move(-1));
    nextButton?.addEventListener('click', () => move(1));
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        activeIndex = Number(dot.dataset.featuredServicesDot);
        update();
        restartTimer();
      });
    });
    track.querySelectorAll('img').forEach((image) => {
      image.addEventListener('load', update);
      if (image.complete) image.decode?.().then(update).catch(() => {});
    });
    slides.forEach((slide) => resizeObserver.observe(slide));

    const onResize = () => {
      update();
      restartTimer();
    };

    mobileQuery.addEventListener('change', onResize);
    window.addEventListener('resize', update);

    instances.set(slider, () => {
      window.clearInterval(timer);
      resizeObserver.disconnect();
      mobileQuery.removeEventListener('change', onResize);
      window.removeEventListener('resize', update);
    });

    update();
    restartTimer();
  };

  const initializeAll = (root = document) => {
    root.querySelectorAll(sliderSelector).forEach(initializeSlider);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(sliderSelector).forEach((slider) => {
      instances.get(slider)?.();
      instances.delete(slider);
    });
  });
})();